import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, Flame, Dumbbell, ShoppingCart, BookOpen, ChevronDown, ShieldCheck, Zap, TrendingUp, AlertTriangle, UtensilsCrossed, MapPin, Download, XIcon } from 'lucide-react';
// --- Reusable Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  text,
  subtext,
  className = ''
}: {
  text: string;
  subtext?: string;
  className?: string;
}) => <motion.a href="https://guias-salud.com/cart/46180894081212:1" target="_blank" rel="noopener noreferrer" whileHover={{
  scale: 1.05
}} whileTap={{
  scale: 0.95
}} className={`inline-flex flex-col items-center justify-center bg-brand-orange text-white font-oswald text-2xl md:text-3xl uppercase tracking-wide py-4 px-8 md:px-12 rounded-lg shadow-[0_0_20px_rgba(255,107,0,0.4)] hover:shadow-[0_0_30px_rgba(255,107,0,0.6)] transition-shadow cursor-pointer text-center ${className}`} data-id="element-1">
    <span data-id="element-2">{text}</span>
    {subtext && <span className="text-sm md:text-base font-inter normal-case text-white/90 mt-1 font-medium" data-id="element-3">
        {subtext}
      </span>}
  </motion.a>;
const Section = ({
  children,
  darker = false,
  id = ''
}: {
  children: React.ReactNode;
  darker?: boolean;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-4 ${darker ? 'bg-brand-darker' : 'bg-brand-dark'}`} data-id="element-4">
    <div className="max-w-3xl mx-auto" data-id="element-5">{children}</div>
  </section>;
const FAQItem = ({
  q,
  a,
  index
}: {
  q: string;
  a: string;
  index: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-brand-dark border border-white/10 rounded-lg overflow-hidden" data-id="element-6">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-7">
        <span className="text-lg font-bold text-white" data-id="element-8">{q}</span>
        <ChevronDown className={`w-5 h-5 text-brand-orange transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-9" />
      </button>
      <AnimatePresence data-id="element-10">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-11">
            <div className="p-6 pt-0 text-brand-gray border-t border-white/5" data-id="element-12">
              {a}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
// --- Main Page Component ---
export function App() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-brand-dark text-brand-gray font-inter selection:bg-brand-orange selection:text-white" data-id="element-13">
      {/* 1. HERO SECTION */}
      <section className="pt-12 pb-16 md:pt-20 md:pb-24 px-4 bg-brand-dark relative overflow-hidden" data-id="element-14">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-orange/10 blur-[120px] rounded-full pointer-events-none" data-id="element-15"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-16">
          <FadeIn data-id="element-17">
            <h1 className="text-5xl md:text-7xl font-oswald font-bold text-brand-orange uppercase leading-[1.1] tracking-tight mb-6" data-id="element-18">
              El Plan de Comidas:
              <br className="hidden md:block" data-id="element-19" /> Músculo en 15 Minutos
            </h1>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-20">
            <h2 className="text-xl md:text-2xl text-white font-medium max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-21">
              La guía definitiva para hombres que quieren eliminar el estado{' '}
              <span className="text-brand-orange font-bold" data-id="element-22">"Skinny-Fat"</span>{' '}
              sin pasar horas en la cocina.
            </h2>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-23">
            <div className="relative max-w-4xl mx-auto mb-12" data-id="element-24">
              <img src="https://cdn.magicpatterns.com/uploads/6vfVqUPDHoqCAc4jjqWaUQ/heylloyd-image-1775834984117.png" alt="Músculo en 15 Minutos en múltiples dispositivos" className="w-full h-auto rounded-xl shadow-2xl" data-id="element-25" />
              {/* Overlay gradient at bottom of image to blend with background */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-brand-dark to-transparent" data-id="element-26"></div>
            </div>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-27">
            <CTAButton text="Comprar el Plan Ahora" subtext="Acceso Inmediato • Pago Seguro" className="w-full sm:w-auto" data-id="element-28" />
          </FadeIn>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <Section darker data-id="element-29">
        <FadeIn data-id="element-30">
          <p className="text-lg md:text-xl leading-relaxed mb-6" data-id="element-31">
            Te miras al espejo antes de ducharte y lo ves: brazos algo delgados,
            pero esa capa de grasa abdominal que no se va. Te sientes{' '}
            <strong className="text-white" data-id="element-32">"flaco-gordo"</strong>.
          </p>
          <p className="text-lg md:text-xl leading-relaxed mb-6" data-id="element-33">
            Has intentado ir al gimnasio, pero tu alimentación es un desastre
            porque simplemente{' '}
            <strong className="text-brand-orange" data-id="element-34">NO tienes tiempo</strong> para
            cocinar platos elaborados de "influencer".
          </p>
          <p className="text-lg md:text-xl leading-relaxed" data-id="element-35">
            Llegas cansado del trabajo, abres la nevera, no ves nada fácil y
            terminas pidiendo comida por app o comiendo cualquier procesado que
            te hace sentir pesado, inflamado y culpable.
          </p>
        </FadeIn>
      </Section>

      {/* 3. THE PROBLEM */}
      <Section data-id="element-36">
        <FadeIn data-id="element-37">
          <h2 className="text-3xl md:text-4xl font-oswald font-bold text-white uppercase mb-8 text-center" data-id="element-38">
            El problema{' '}
            <span className="text-brand-orange" data-id="element-39">
              no es tu falta de voluntad
            </span>
          </h2>
          <p className="text-lg leading-relaxed mb-6" data-id="element-40">
            Es que la mayoría de las dietas están diseñadas para personas que
            viven en la cocina o tienen un chef privado. Te piden ingredientes
            raros, técnicas complejas y limpiezas eternas de sartenes.
          </p>
          <p className="text-lg leading-relaxed mb-6" data-id="element-41">
            Si trabajas 8-10 horas al día, estudias o tienes responsabilidades,
            una dieta de "pollo y brócoli hervido" es una sentencia de muerte
            para tu motivación.
          </p>
          <div className="bg-brand-darker border border-brand-orange/20 p-6 rounded-lg mt-8" data-id="element-42">
            <p className="text-lg leading-relaxed text-white font-medium" data-id="element-43">
              Por eso sigues atrapado en el limbo del "skinny-fat": suficiente
              comida para no estar flaco, pero no la nutrición correcta para
              estar marcado.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <Section darker data-id="element-44">
        <FadeIn data-id="element-45">
          <h2 className="text-3xl md:text-4xl font-oswald font-bold text-white uppercase mb-10 text-center" data-id="element-46">
            Por qué lo que has intentado{' '}
            <span className="text-red-500" data-id="element-47">ha fallado</span>
          </h2>

          <div className="space-y-6" data-id="element-48">
            {['Las apps de conteo de calorías son estresantes y terminas abandonándolas a los 3 días.', 'Comprar comida "saludable" fuera te cuesta $15-$20 por plato y suele estar llena de aceites inflamatorios.', 'Los batidos de proteína solos no construyen músculo si tu metabolismo está "apagado" por falta de comida real.', 'Las recetas de internet dicen "fáciles", pero ignoran el tiempo que tardas lavando, cortando y limpiando.'].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1 + 0.2
          }} className="flex items-start gap-4 bg-brand-dark p-5 rounded-lg border border-white/5" data-id="element-49">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-50" />
                <p className="text-lg" data-id="element-51">{item}</p>
              </motion.div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 5. THE UNIQUE MECHANISM */}
      <Section data-id="element-52">
        <FadeIn data-id="element-53">
          <div className="text-center mb-10" data-id="element-54">
            <Zap className="w-12 h-12 text-brand-orange mx-auto mb-4" data-id="element-55" />
            <h2 className="text-3xl md:text-5xl font-oswald font-bold text-white uppercase mb-6" data-id="element-56">
              La Solución:{' '}
              <span className="text-brand-orange" data-id="element-57">La Matriz de 3 Pasos</span>
            </h2>
            <p className="text-xl text-white/80 font-medium" data-id="element-58">
              (Proteína + Soporte + Activador)
            </p>
          </div>

          <p className="text-lg leading-relaxed mb-6" data-id="element-59">
            No es un recetario tradicional; es un{' '}
            <strong data-id="element-60">sistema de ingeniería logística</strong>. Aplicamos la regla
            de "5 Ingredientes / 15 Minutos".
          </p>
          <p className="text-lg leading-relaxed mb-8" data-id="element-61">
            Si una comida requiere más de 5 elementos o más de un cuarto de hora
            de tu vida, queda fuera del plan. Es nutrición de alta densidad
            diseñada para el ritmo de vida del hombre moderno en USA.
          </p>

          <div className="flex justify-center mt-10" data-id="element-62">
            <CTAButton text="Quiero este sistema" data-id="element-63" />
          </div>
        </FadeIn>
      </Section>

      {/* 6. WHAT YOU GET */}
      <Section darker data-id="element-64">
        <FadeIn data-id="element-65">
          <h2 className="text-3xl md:text-4xl font-oswald font-bold text-white uppercase mb-12 text-center" data-id="element-66">
            Lo que obtienes hoy
          </h2>

          <div className="grid gap-6" data-id="element-67">
            {[{
            icon: <BookOpen className="w-8 h-8 text-brand-orange" data-id="element-68" />,
            title: 'Manual de Hipertrofia Minimalista',
            desc: 'El sistema para entender qué comer según tu nivel de actividad.'
          }, {
            icon: <TrendingUp className="w-8 h-8 text-brand-orange" data-id="element-69" />,
            title: 'La Matriz de Combinación Proteica',
            desc: 'Crea más de 20 variantes de comidas solo combinando columnas.'
          }, {
            icon: <Clock className="w-8 h-8 text-brand-orange" data-id="element-70" />,
            title: 'Recetario "Flash"',
            desc: 'Desayunos, almuerzos y cenas reales que se hacen en menos tiempo del que tarda en llegar el repartidor.'
          }, {
            icon: <ShoppingCart className="w-8 h-8 text-brand-orange" data-id="element-71" />,
            title: 'Lista de Compras de Fricción Cero',
            desc: "Optimizada para Walmart, Costco, Target o Trader Joe's."
          }, {
            icon: <MapPin className="w-8 h-8 text-brand-orange" data-id="element-72" />,
            title: 'Guía de Supervivencia en Gasolineras',
            desc: 'Qué comprar en 7-Eleven o Wawa cuando estás en la calle y necesitas proteína real.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-brand-dark p-6 rounded-xl border border-brand-orange/20 flex flex-col sm:flex-row gap-5 items-start" data-id="element-73">
                <div className="bg-brand-orange/10 p-3 rounded-lg flex-shrink-0" data-id="element-74">
                  {feature.icon}
                </div>
                <div data-id="element-75">
                  <h3 className="text-xl font-oswald font-bold text-white uppercase tracking-wide mb-2" data-id="element-76">
                    {feature.title}
                  </h3>
                  <p className="text-brand-gray" data-id="element-77">{feature.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <Section data-id="element-78">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-79">
          <FadeIn delay={0.1} data-id="element-80">
            <div className="bg-brand-darker p-8 rounded-xl border-t-4 border-green-500 h-full" data-id="element-81">
              <h3 className="text-2xl font-oswald font-bold text-white uppercase mb-6 flex items-center gap-3" data-id="element-82">
                <CheckCircle2 className="text-green-500 w-8 h-8" data-id="element-83" />
                Esto ES para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-84">
                {['Hombres 20+ que trabajan o estudian a tiempo completo.', 'Principiantes en el gimnasio que no saben cómo cuadrar su comida.', 'Personas cansadas de verse "skinny-fat" y que quieren definición real.', 'Hombres solteros que odian lavar platos y complicaciones culinarias.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-85">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" data-id="element-86" />
                    <span data-id="element-87">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-88">
            <div className="bg-brand-darker p-8 rounded-xl border-t-4 border-red-500 h-full" data-id="element-89">
              <h3 className="text-2xl font-oswald font-bold text-white uppercase mb-6 flex items-center gap-3" data-id="element-90">
                <XCircle className="text-red-500 w-8 h-8" data-id="element-91" />
                Esto NO es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-92">
                {['Personas que buscan recetas de "Gourmet" o alta cocina.', 'Gente que tiene 2 horas libres cada tarde para cocinar.', 'Aquellos que esperan resultados mágicos sin pisar el gimnasio ni una vez.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-93">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" data-id="element-94" />
                    <span data-id="element-95">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* 8. HOW IT WORKS */}
      <Section darker data-id="element-96">
        <FadeIn data-id="element-97">
          <h2 className="text-3xl md:text-4xl font-oswald font-bold text-white uppercase mb-12 text-center" data-id="element-98">
            Cómo funciona
          </h2>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-99">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-brand-orange/30 z-0" data-id="element-100"></div>

            {[{
            step: '1',
            icon: <Download className="w-8 h-8 text-white" data-id="element-101" />,
            title: 'Descarga el PDF',
            desc: 'Recibes el acceso inmediato a todo el sistema en tu móvil.'
          }, {
            step: '2',
            icon: <ShoppingCart className="w-8 h-8 text-white" data-id="element-102" />,
            title: 'Compra lo Exacto',
            desc: 'Usa la lista de 5 minutos en el súper para tener todo lo que necesitas para la semana.'
          }, {
            step: '3',
            icon: <UtensilsCrossed className="w-8 h-8 text-white" data-id="element-103" />,
            title: 'Cocina en 15 min',
            desc: 'Elige una combinación de la Matriz, pon el cronómetro y disfruta de comida que construye músculo de verdad.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-104">
                <div className="w-24 h-24 rounded-full bg-brand-dark border-4 border-brand-orange flex items-center justify-center mb-6 shadow-[0_0_15px_rgba(255,107,0,0.3)]" data-id="element-105">
                  {item.icon}
                </div>
                <div className="text-brand-orange font-oswald font-bold text-xl mb-2" data-id="element-106">
                  PASO {item.step}
                </div>
                <h3 className="text-2xl font-oswald font-bold text-white uppercase mb-3" data-id="element-107">
                  {item.title}
                </h3>
                <p className="text-brand-gray" data-id="element-108">{item.desc}</p>
              </motion.div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 9. PROOF */}
      <Section data-id="element-109">
        <FadeIn data-id="element-110">
          <div className="bg-gradient-to-br from-brand-orange/20 to-brand-darker border border-brand-orange/30 p-8 md:p-12 rounded-2xl text-center relative overflow-hidden" data-id="element-111">
            <Flame className="absolute -right-10 -top-10 w-48 h-48 text-brand-orange/10 rotate-12" data-id="element-112" />

            <h2 className="text-3xl md:text-4xl font-oswald font-bold text-white uppercase mb-6 relative z-10" data-id="element-113">
              El Cambio en <span className="text-brand-orange" data-id="element-114">7 Días</span>
            </h2>
            <p className="text-xl leading-relaxed text-white/90 relative z-10 max-w-2xl mx-auto" data-id="element-115">
              En la primera semana siguiendo el plan, notarás menos inflamación
              abdominal, niveles de energía estables durante la tarde (adiós al
              "crash" de las 3 PM) y una recuperación muscular mucho más rápida
              tras tus entrenamientos.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* 10. FAQ */}
      <Section darker data-id="element-116">
        <FadeIn data-id="element-117">
          <h2 className="text-3xl md:text-4xl font-oswald font-bold text-white uppercase mb-10 text-center" data-id="element-118">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-4" data-id="element-119">
            {[{
            q: '¿Necesito suplementos?',
            a: 'No, este plan se basa en comida real de supermercado.'
          }, {
            q: '¿Es muy caro comprar esta comida?',
            a: 'Al contrario, gastarás menos que pidiendo comida fuera o comprando ingredientes "especiales" de dieta.'
          }, {
            q: '¿Tengo que saber cocinar?',
            a: 'Si sabes encender una sartén o usar un microondas, puedes seguir este plan.'
          }, {
            q: '¿Sirve para ganar músculo o perder grasa?',
            a: 'Sirve para recomposición corporal (el fin del skinny-fat), que es ganar músculo mientras mantienes la grasa bajo control.'
          }].map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} index={i} data-id="element-120" />)}
          </div>
        </FadeIn>
      </Section>

      {/* 11. OFFER + PRICE BLOCK */}
      <Section id="comprar" data-id="element-121">
        <FadeIn data-id="element-122">
          <div className="max-w-2xl mx-auto bg-brand-darker border-2 border-brand-orange rounded-2xl p-8 md:p-12 text-center shadow-[0_0_40px_rgba(255,107,0,0.15)]" data-id="element-123">
            <h2 className="text-3xl md:text-4xl font-oswald font-bold text-white uppercase mb-6" data-id="element-124">
              Obtén el Sistema Completo Hoy
            </h2>

            <p className="text-lg mb-8" data-id="element-125">
              Hoy puedes obtener el sistema completo "Plan de Comidas: Músculo
              en 15 Minutos" por un pago único. Sin suscripciones, sin cobros
              ocultos.
            </p>

            <div className="mb-10" data-id="element-126">
              <span className="text-brand-gray uppercase tracking-widest text-sm font-bold block mb-2" data-id="element-127">
                Precio Exclusivo
              </span>
              <div className="text-6xl md:text-7xl font-oswald font-bold text-brand-orange" data-id="element-128">
                $6.39 <span className="text-2xl text-white" data-id="element-129">USD</span>
              </div>
            </div>

            <CTAButton text="Comprar el Plan Ahora" subtext="(Acceso Inmediato)" className="w-full mb-6" data-id="element-130" />

            <div className="flex flex-col items-center gap-3 text-sm text-brand-gray" data-id="element-131">
              <div className="flex items-center gap-2" data-id="element-132">
                <ShieldCheck className="w-5 h-5 text-green-500" data-id="element-133" />
                <span data-id="element-134">Pago seguro cifrado</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-135">
                <Clock className="w-5 h-5 text-brand-orange" data-id="element-136" />
                <span data-id="element-137">Acceso de por vida desde cualquier dispositivo</span>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 12. FOOTER */}
      <footer className="bg-black py-8 text-center text-sm text-brand-gray/60 border-t border-white/5" data-id="element-138">
        <div className="max-w-3xl mx-auto px-4" data-id="element-139">
          <p className="mb-4" data-id="element-140">
            © {new Date().getFullYear()} Músculo en 15 Minutos. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-141">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-brand-orange transition-colors underline underline-offset-2 cursor-pointer" data-id="element-142">
              Política de Privacidad
            </button>
            <span className="text-white/20" data-id="element-143">|</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-brand-orange transition-colors underline underline-offset-2 cursor-pointer" data-id="element-144">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-xs max-w-xl mx-auto opacity-50" data-id="element-145">
            Este producto no sustituye el consejo médico profesional. Los
            resultados pueden variar dependiendo del esfuerzo individual y la
            consistencia.
          </p>
        </div>
      </footer>

      {/* Privacy & Terms Modals */}
      <AnimatePresence data-id="element-146">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-147">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-brand-darker border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-148">
              <div className="sticky top-0 bg-brand-darker border-b border-white/10 p-6 flex items-center justify-between rounded-t-2xl z-10" data-id="element-149">
                <h3 className="text-2xl font-oswald font-bold text-white uppercase" data-id="element-150">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-brand-gray hover:text-white transition-colors p-1" data-id="element-151">
                  <XIcon className="w-6 h-6" data-id="element-152" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-brand-gray text-sm leading-relaxed" data-id="element-153">
                {activeModal === 'privacy' ? <>
                    <p className="text-white font-medium" data-id="element-154">
                      Última actualización:{' '}
                      {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-155">
                      1. Información que Recopilamos
                    </h4>
                    <p data-id="element-156">
                      Recopilamos únicamente la información necesaria para
                      procesar tu compra: nombre, dirección de correo
                      electrónico y datos de pago. No almacenamos información de
                      tarjetas de crédito directamente; los pagos son procesados
                      por proveedores de pago seguros de terceros.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-157">
                      2. Uso de la Información
                    </h4>
                    <p data-id="element-158">
                      Tu información se utiliza exclusivamente para: procesar tu
                      pedido, enviarte el producto digital adquirido y
                      comunicarte actualizaciones importantes sobre tu compra.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-159">
                      3. Protección de Datos
                    </h4>
                    <p data-id="element-160">
                      Implementamos medidas de seguridad estándar de la
                      industria para proteger tu información personal.
                      Utilizamos cifrado SSL en todas las transacciones.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-161">
                      4. Compartir Información
                    </h4>
                    <p data-id="element-162">
                      No vendemos, intercambiamos ni transferimos tu información
                      personal a terceros, excepto a proveedores de servicios de
                      confianza que nos asisten en la operación del sitio web y
                      el procesamiento de pagos.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-163">
                      5. Cookies
                    </h4>
                    <p data-id="element-164">
                      Podemos utilizar cookies para mejorar tu experiencia en
                      nuestro sitio. Puedes configurar tu navegador para
                      rechazar cookies, aunque esto puede afectar algunas
                      funcionalidades.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-165">
                      6. Tus Derechos
                    </h4>
                    <p data-id="element-166">
                      Tienes derecho a acceder, rectificar o eliminar tus datos
                      personales en cualquier momento. Para ejercer estos
                      derechos, contáctanos a través de los canales disponibles.
                    </p>
                  </> : <>
                    <p className="text-white font-medium" data-id="element-167">
                      Última actualización:{' '}
                      {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-168">
                      1. Aceptación de los Términos
                    </h4>
                    <p data-id="element-169">
                      Al comprar y acceder a "Plan de Comidas: Músculo en 15
                      Minutos", aceptas estos términos y condiciones en su
                      totalidad. Si no estás de acuerdo, no realices la compra.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-170">
                      2. Producto Digital
                    </h4>
                    <p data-id="element-171">
                      Este es un producto digital (PDF) que se entrega de forma
                      inmediata tras la confirmación del pago. Recibirás acceso
                      de por vida al contenido adquirido.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-172">
                      3. Uso Personal
                    </h4>
                    <p data-id="element-173">
                      El contenido es para uso personal únicamente. Queda
                      prohibida la reproducción, distribución, modificación o
                      reventa del material sin autorización expresa por escrito.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-174">
                      4. Descargo de Responsabilidad
                    </h4>
                    <p data-id="element-175">
                      Este producto es informativo y educativo. No sustituye el
                      consejo médico, nutricional o de un profesional de la
                      salud. Los resultados pueden variar según el esfuerzo
                      individual, la consistencia y las condiciones de salud de
                      cada persona.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-176">
                      5. Política de Reembolso
                    </h4>
                    <p data-id="element-177">
                      Debido a la naturaleza digital del producto y su entrega
                      inmediata, no se ofrecen reembolsos una vez completada la
                      descarga. Si experimentas problemas técnicos con la
                      descarga, te ayudaremos a resolverlos.
                    </p>
                    <h4 className="text-lg font-bold text-white mt-6" data-id="element-178">
                      6. Limitación de Responsabilidad
                    </h4>
                    <p data-id="element-179">
                      No nos hacemos responsables de lesiones, daños o pérdidas
                      derivadas del uso de la información contenida en este
                      producto. El usuario asume toda la responsabilidad por sus
                      decisiones de salud y nutrición.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}